import { useCallback, useEffect, useRef, useState } from 'react'
import { Minus, Phone, GripVertical, Maximize2 } from 'lucide-react'
import { useAppStore } from '@/store'
import { IN_APP_PANEL_SIZE } from '@/store/inAppMeetingSlice'
import { apiService } from '@/service/api'
import MeetingBot from '@/routes/MeetingBot'
import { CallSurface } from '@/routes/MeetingBot/CallSurface'
import { isFabricEmbedMode } from '@/lib/fabricEmbedAuth'
import { closeCallWindow, onCallWindowClosed } from '@/lib/meetingCallWindow'
import {
  applyCallHostEvent,
  initialCallUiState,
  openPanelChannel,
  type CallUiState,
} from '@/lib/callChannel'

const PANEL_WIDTH = IN_APP_PANEL_SIZE.width
const PANEL_HEIGHT = IN_APP_PANEL_SIZE.height
const PILL_SIZE = 64

/**
 * Floating PiP-style panel that hosts an active in-app meeting bot
 * session. Survives route changes — mounted once at the router root.
 *
 * - Renders nothing until `inAppMeetingSlice.activeToken` is set.
 * - Drag the header bar to reposition; position persists in the slice.
 * - Minimize collapses into a small pill (avatar + listening indicator).
 * - End-call button tears down the WebRTC session and clears the slice.
 */
/**
 * Renders the call from state relayed by the popup that owns the microphone.
 *
 * Embedded in Fabric the call cannot run in this frame — the portal's iframe does
 * not delegate `microphone` and Permissions Policy is inherited, so an in-frame
 * session connects and is permanently deaf. The popup holds the mic and the peer
 * connection; this is the UI, rendering the SAME `<CallSurface>` the non-embedded
 * path uses. The user therefore sees one call, in the board, where they are
 * working — and the popup being buried behind the browser window stops mattering.
 */
const RelayedCall = ({ token, onEnded }: { token: string; onEnded: () => void }) => {
  const [state, setState] = useState<CallUiState>(initialCallUiState)
  const stopRetryRef = useRef<() => void>(() => {})

  useEffect(() => {
    const channel = openPanelChannel(token, (event) => {
      stopRetryRef.current()
      setState((prev) => applyCallHostEvent(prev, event))
      if (event.t === 'bye') onEnded()
    })

    // The host may have connected long before this panel mounted — or remounted
    // after a board-iframe reload — so ask for a snapshot rather than waiting for
    // whatever event happens to fire next.
    channel.post({ t: 'hello' })

    // The popup may still be loading, so retry until it answers instead of leaving
    // the panel on "Connecting…".
    const retry = window.setInterval(() => channel.post({ t: 'hello' }), 1000)
    stopRetryRef.current = () => window.clearInterval(retry)

    return () => {
      window.clearInterval(retry)
      channel.close()
    }
  }, [token, onEnded])

  // /listenon and /listenoff from the panel (FS-711): the popup owns the mic, but the command
  // goes to the bot over HTTP with the token, so the panel sends it itself.
  const toggleListening = useCallback(
    (listening: boolean) => {
      apiService.setMeetingListening(token, listening).catch((err: unknown) => {
        console.error('[InAppMeetingPanel] Listening command failed:', err)
      })
    },
    [token]
  )

  return (
    <div className="w-full h-full bg-gray-900 flex items-center justify-center">
      <div className="flex flex-col items-center justify-center gap-2 px-2 py-2">
        <CallSurface state={state} compact avatarSize={200} onToggleListening={toggleListening} />
      </div>
    </div>
  )
}

const InAppMeetingPanel = () => {
  const activeToken = useAppStore((s) => s.activeToken)
  const isMinimized = useAppStore((s) => s.isMinimized)
  const panelPosition = useAppStore((s) => s.panelPosition)
  const agentName = useAppStore((s) => s.agentName)
  const toggleMinimize = useAppStore((s) => s.toggleMinimize)
  const setPanelPosition = useAppStore((s) => s.setPanelPosition)
  const endInAppMeeting = useAppStore((s) => s.endInAppMeeting)

  const dragStateRef = useRef<{
    pointerId: number
    offsetX: number
    offsetY: number
  } | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Only start drag on primary button; ignore clicks bubbling up
      // from the buttons inside the header.
      if (e.button !== 0) return
      const target = e.target as HTMLElement
      if (target.closest('[data-no-drag]')) return

      const rect = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect()
      dragStateRef.current = {
        pointerId: e.pointerId,
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top,
      }
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
      setIsDragging(true)
    },
    []
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragStateRef.current
      if (!drag || drag.pointerId !== e.pointerId) return
      const w = isMinimized ? PILL_SIZE : PANEL_WIDTH
      const h = isMinimized ? PILL_SIZE : PANEL_HEIGHT
      const nextX = Math.min(
        Math.max(0, e.clientX - drag.offsetX),
        Math.max(0, window.innerWidth - w)
      )
      const nextY = Math.min(
        Math.max(0, e.clientY - drag.offsetY),
        Math.max(0, window.innerHeight - h)
      )
      setPanelPosition({ x: nextX, y: nextY })
    },
    [isMinimized, setPanelPosition]
  )

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragStateRef.current
      if (!drag || drag.pointerId !== e.pointerId) return
      try {
        ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
      } catch {
        // releasePointerCapture can throw if capture was lost — safe to ignore.
      }
      dragStateRef.current = null
      setIsDragging(false)
    },
    []
  )

  // Re-clamp panel position when the viewport shrinks so it doesn't
  // drift off-screen on resize.
  useEffect(() => {
    if (!activeToken) return
    const onResize = () => {
      const w = isMinimized ? PILL_SIZE : PANEL_WIDTH
      const h = isMinimized ? PILL_SIZE : PANEL_HEIGHT
      const x = Math.min(panelPosition.x, Math.max(0, window.innerWidth - w))
      const y = Math.min(panelPosition.y, Math.max(0, window.innerHeight - h))
      if (x !== panelPosition.x || y !== panelPosition.y) {
        setPanelPosition({ x, y })
      }
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [activeToken, isMinimized, panelPosition.x, panelPosition.y, setPanelPosition])

  // In embed mode the call lives in a popup, so closing that window is the user
  // ending the call. Without this the panel would sit there claiming a call is
  // running while the session leaked server-side.
  const isEmbedded = isFabricEmbedMode()
  useEffect(() => {
    if (!activeToken || !isEmbedded) return
    return onCallWindowClosed(() => {
      void endInAppMeeting()
    })
  }, [activeToken, isEmbedded, endInAppMeeting])

  if (!activeToken) {
    return null
  }

  const handleEnd = () => {
    // Order matters: close the window first so its watcher sees a deliberate
    // teardown and does not re-enter endInAppMeeting.
    closeCallWindow()
    void endInAppMeeting()
  }

  if (isMinimized) {
    return (
      <div
        role="dialog"
        aria-label="In-app meeting (minimized)"
        style={{
          position: 'fixed',
          left: panelPosition.x,
          top: panelPosition.y,
          width: PILL_SIZE,
          height: PILL_SIZE,
          zIndex: 9999,
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
      >
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="w-full h-full rounded-full bg-gray-900 shadow-xl border-2 border-blue-500 flex items-center justify-center relative overflow-hidden"
          title={agentName ? `In call with ${agentName}` : 'In-app call'}
        >
          {/* Listening / activity pulse */}
          <span
            aria-hidden
            className="absolute inset-0 rounded-full animate-ping bg-blue-500/30"
          />
          <button
            data-no-drag
            onClick={toggleMinimize}
            className="relative z-10 w-full h-full flex items-center justify-center text-white"
            aria-label="Restore in-app meeting"
          >
            <Maximize2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      role="dialog"
      aria-label="In-app meeting"
      style={{
        position: 'fixed',
        left: panelPosition.x,
        top: panelPosition.y,
        width: PANEL_WIDTH,
        height: PANEL_HEIGHT,
        zIndex: 9999,
      }}
      className="rounded-lg shadow-2xl border border-gray-700 bg-gray-900 overflow-hidden flex flex-col"
    >
      {/* Header / drag handle */}
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700 select-none"
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div className="flex items-center gap-2 text-white text-sm">
          <GripVertical className="w-4 h-4 text-gray-400" />
          <span className="font-medium truncate max-w-[200px]">
            {agentName ?? 'In-app call'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            data-no-drag
            onClick={toggleMinimize}
            className="p-1 rounded hover:bg-gray-700 text-gray-300 hover:text-white"
            aria-label="Minimize in-app meeting"
            title="Minimize"
          >
            <Minus className="w-4 h-4" />
          </button>
          <button
            data-no-drag
            onClick={handleEnd}
            className="p-1 rounded bg-red-600 hover:bg-red-700 text-white"
            aria-label="End in-app meeting"
            title="End call"
          >
            <Phone className="w-4 h-4 rotate-[135deg]" />
          </button>
        </div>
      </div>

      {/* Body — in-frame WebRTC normally; a launcher when embedded in Fabric,
          where this frame is not permitted to use the microphone. */}
      <div className="flex-1 min-h-0">
        {isEmbedded ? (
          <RelayedCall token={activeToken} onEnded={handleEnd} />
        ) : (
          <MeetingBot token={activeToken} embedded />
        )}
      </div>
    </div>
  )
}

export default InAppMeetingPanel
